// src/components/Calendar/TradingEconomicsCalendar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { fetchPutResponse } from "../../utilities/ApiUtilities";
import Select from "react-select";
import "./EconomicCalendar.css";
import logoMark from "../../../assets/logo-collapsed.png";
import {
  TRADING_ECONOMICS_COUNTRY_OPTIONS as countryOptions,
} from "./TradingEconomicsCountries";
import {
  autoScrollSelectComponents,
  prioritizeSelectedOption,
} from "../Common/SelectMenuScroll";

function parseApiUtcToLocalDate(v) {
  if (v == null) return null;
  if (v instanceof Date) return new Date(v.getTime());
  if (typeof v === "number") {
    // 10 digits: seconds; 13 digits: ms
    return new Date(v < 1e12 ? v * 1000 : v);
  }
  let s = String(v).trim();

  // Normalize "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?/.test(s)) {
    s = s.replace(" ", "T");
  }

  // If there is no timezone info, append Z to treat as UTC.
  const hasTZ = /[Zz]|[-+]\d{2}:\d{2}$/.test(s);
  if (!hasTZ) s = s + "Z";

  return new Date(s); // Now Date is the correct instant; formatting will be local.
}

/* ------------------------------- Date helpers ------------------------------- */
const fmtMonthYear = (d) =>
  new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(
    d
  );

const fmtWeekdayShort = (i) =>
  new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(
    new Date(2025, 0, 5 + i)
  ); // Sun..Sat

// Accepts a Date or ISO string. Always returns localized time string.
const fmtTime = (input) => {
  const date = input instanceof Date ? input : new Date(input);
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// YYYY-MM-DD in *local* time for grouping/labels
const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0); // local midnight
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999); // local end of day
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
}
// Build a 6-week (42-cell) grid like Google Calendar (Sun..Sat)
function buildMonthMatrix(anyDayInMonth) {
  const first = startOfMonth(anyDayInMonth);
  const startIdx = first.getDay(); // 0=Sun
  const gridStart = addDays(first, -startIdx);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/* --------------------------- Main Calendar Component --------------------------- */
// `showLogo` adds the EMAlpha mark to the title (partner embeds have no sidebar).
export default function TradingEconomicsCalendar({ showLogo = false }) {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState("day"); // "month" | "day"
  const [cursorMonth, setCursorMonth] = useState(startOfMonth(today)); // month being viewed in grid
  const [selectedDate, setSelectedDate] = useState(startOfDay(today));
  const [country, setCountry] = useState("United States");
  const selectedCountryOption = useMemo(
    () => ({ value: country, label: country }),
    [country],
  );
  const prioritizedCountryOptions = useMemo(
    () => prioritizeSelectedOption(countryOptions, selectedCountryOption),
    [selectedCountryOption],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [eventsByDate, setEventsByDate] = useState({}); // { 'YYYY-MM-DD': [events-with-local-fields] }

  // Build date range to fetch based on current view (in *local*), then send as UTC ISO
  const { fetchStart, fetchEnd } = useMemo(() => {
    if (view === "day") {
      return {
        fetchStart: startOfDay(selectedDate),
        fetchEnd: endOfDay(selectedDate),
      };
    }
    return {
      fetchStart: startOfMonth(cursorMonth),
      fetchEnd: endOfMonth(cursorMonth),
    };
  }, [view, cursorMonth, selectedDate]);

  // Load events whenever view/month/selectedDate/country changes
  useEffect(() => {
    let alive = true;
    async function load() {
      setIsLoading(true);
      try {
        const payload = {
          country: country.toLowerCase(), // backend expects lowercase ("united states")
          // Send UTC instants (API is UTC)
          start_date: fetchStart.toISOString(),
          end_date: fetchEnd.toISOString(),
        };

        // ---- Real API call ----
        const response = await fetchPutResponse(
          "trading_economics/get_events",
          payload
        );
        const { results } = await response.json();

        // ---- If you want to test with mock instead, comment out two lines above and use:
        // const { results } = await mockFetchEvents(payload);

        if (!alive) return;

        const localized = (results || []).map((ev) => {
          // Always parse as UTC, then we display in LOCAL
          const localDate = parseApiUtcToLocalDate(ev.trigger_time);
          return {
            ...ev,
            trigger_time_local: localDate,
            __date_key_local: dateKey(localDate),
          };
        });

        // Group by local calendar day
        const grouped = {};
        for (const ev of localized) {
          const key = ev.__date_key_local;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(ev);
        }

        // Sort each day by local time
        Object.values(grouped).forEach((arr) =>
          arr.sort(
            (a, b) =>
              a.trigger_time_local.getTime() - b.trigger_time_local.getTime()
          )
        );

        setEventsByDate(grouped);
      } catch (e) {
        console.error(e);
        setEventsByDate({});
      } finally {
        if (alive) setIsLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [view, fetchStart, fetchEnd, country]);

  const monthCells = useMemo(
    () => buildMonthMatrix(cursorMonth),
    [cursorMonth]
  );
  const selectedKey = dateKey(selectedDate);
  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < 6; i++) rows.push(monthCells.slice(i * 7, i * 7 + 7));
    return rows;
  }, [monthCells]);

  function goPrev() {
    if (view === "day") {
      setSelectedDate(addDays(selectedDate, -1));
    } else {
      const prev = new Date(cursorMonth);
      prev.setMonth(prev.getMonth() - 1);
      setCursorMonth(startOfMonth(prev));
    }
  }
  function goNext() {
    if (view === "day") {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      const next = new Date(cursorMonth);
      next.setMonth(next.getMonth() + 1);
      setCursorMonth(startOfMonth(next));
    }
  }
  function goToday() {
    setSelectedDate(startOfDay(today));
    setCursorMonth(startOfMonth(today));
    setView("day");
  }
  function openDay(d) {
    setSelectedDate(startOfDay(d));
    setView("day");
  }

  const dayEvents = eventsByDate[selectedKey] ?? [];

  const statusClass = (status) => {
    const s = String(status || "").toLowerCase();
    return s === "completed" || s === "pending" ? `is-${s}` : "";
  };

  return (
    <div className="ec-page">
      <div className="ec-shell">
        <div className="ec-header">
          <div>
            <h1 className="ec-title">
              {showLogo && <img src={logoMark} alt="EMAlpha" className="ec-logo" />}
              Economic Calendar
            </h1>
            <p className="ec-subtitle">
              Scheduled releases and events for {country}
            </p>
          </div>
        </div>

        {/* ----------------------------- Toolbar / Filters ----------------------------- */}
        <div className="ec-toolbar">
          <div className="ec-group">
            <button className="ec-btn" onClick={goPrev} title="Previous">
              ‹
            </button>
            <button className="ec-btn" onClick={goToday} title="Today">
              Today
            </button>
            <button className="ec-btn" onClick={goNext} title="Next">
              ›
            </button>
          </div>

          <div className="ec-group">
            <button
              className={`ec-btn ${view === "day" ? "is-active" : ""}`}
              onClick={() => setView("day")}
            >
              Day
            </button>
            <button
              className={`ec-btn ${view === "month" ? "is-active" : ""}`}
              onClick={() => setView("month")}
            >
              Month
            </button>
          </div>

          <div className="ec-country">
            <span className="ec-label">Country</span>
            <div className="ec-country-select">
              <Select
                options={prioritizedCountryOptions}
                value={selectedCountryOption}
                onChange={(selected) => setCountry(selected.value)}
                isSearchable
                classNamePrefix="country-select"
                components={autoScrollSelectComponents}
                styles={{
                  menu: (base) => ({
                    ...base,
                    borderRadius: 10,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>

          <div className="ec-range">
            {view === "day"
              ? selectedDate.toDateString()
              : fmtMonthYear(cursorMonth)}
          </div>
        </div>

        {/* --------------------------------- Content --------------------------------- */}
        <div className="ec-panel">
          {view === "month" ? (
            <MonthGrid
              weeks={weeks}
              cursorMonth={cursorMonth}
              selectedDate={selectedDate}
              eventsByDate={eventsByDate}
              isLoading={isLoading}
              onOpenDay={openDay}
              statusClass={statusClass}
            />
          ) : (
            <DayView
              date={selectedDate}
              events={dayEvents}
              isLoading={isLoading}
              statusClass={statusClass}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Month Grid --------------------------------- */
function MonthGrid({
  weeks,
  cursorMonth,
  selectedDate,
  eventsByDate,
  isLoading,
  onOpenDay,
  statusClass,
}) {
  const weekdayHeaders = Array.from({ length: 7 }, (_, i) =>
    fmtWeekdayShort(i)
  );
  const todayKey = dateKey(new Date());
  const selectedKey = dateKey(selectedDate);

  return (
    <div>
      <div className="ec-grid">
        {weekdayHeaders.map((w, i) => (
          <div key={i} className="ec-weekday">
            {w}
          </div>
        ))}
        {weeks.flat().map((day, idx) => {
          const key = dateKey(day);
          const evs = eventsByDate[key] || [];
          const cls = [
            "ec-cell",
            day.getMonth() !== cursorMonth.getMonth() && "is-outside",
            key === selectedKey && "is-selected",
            key === todayKey && "is-today",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={idx} className={cls} onClick={() => onOpenDay(day)}>
              <div className="ec-cell-head">
                <span>{day.getDate()}</span>
                {key === todayKey && <span className="ec-today-tag">Today</span>}
              </div>
              {evs.slice(0, 3).map((ev) => (
                <div
                  key={ev.id}
                  className={`ec-pill ${statusClass(ev.status)}`}
                  title={`${ev.title} — ${fmtTime(ev.trigger_time_local)}`}
                >
                  <span className="ec-pill-title">{ev.title}</span>
                  <span className="ec-pill-time">
                    {fmtTime(ev.trigger_time_local)}
                  </span>
                </div>
              ))}
              {evs.length > 3 && (
                <div className="ec-more">+{evs.length - 3} more</div>
              )}
            </div>
          );
        })}
      </div>

      {isLoading && <div className="ec-loading">Loading…</div>}
    </div>
  );
}

/* ---------------------------------- Day View ---------------------------------- */
function DayView({ date, events = [], isLoading, statusClass }) {
  return (
    <div>
      <div className="ec-panel-head">
        <h2 className="ec-panel-title">Events for {date.toDateString()}</h2>
        <span className="ec-count">{events.length} event(s)</span>
      </div>

      {isLoading ? (
        <div className="ec-loading">Loading…</div>
      ) : events.length === 0 ? (
        <div className="ec-empty">No events</div>
      ) : (
        <div className="ec-list">
          {events.map((ev) => (
            <div key={ev.id} className="ec-event">
              <div>
                <div className="ec-event-title">{ev.title}</div>
                <div className="ec-event-meta">
                  {[
                    fmtTime(ev.trigger_time_local),
                    ev.category,
                    ev.country,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </div>
              </div>
              {ev.status && (
                <span className={`ec-chip ${statusClass(ev.status)}`}>
                  {ev.status}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
